import { useState, useEffect } from 'react'
import { adminApi } from '../services/api'
import {
  Settings,
  Clock,
  ToggleLeft,
  ToggleRight,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  FlaskConical,
  Target,
  GitBranch,
  Lightbulb
} from 'lucide-react'

export default function SettingsPage() {
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Form states
  const [trial, setTrial] = useState({
    durationDays: 30,
    gracePeriodDays: 30,
    limits: { maxMinori: 10, maxEducatori: 3, maxCoordinatori: 1 }
  })
  const [features, setFeatures] = useState({
    registrationEnabled: true,
    maintenanceMode: false,
    maintenanceMessage: '',
    betaMode: true,
    paymentsEnabled: false,
    emailEnabled: false,
    pepExperimental: false,
    quickCheckinEnabled: true,
    attivitaGruppoEnabled: true,
    smartAlertsEnabled: true,
    prepColloquioEnabled: true
  })
  const [expStats, setExpStats] = useState(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const [settingsRes, statsRes] = await Promise.allSettled([
        adminApi.getSettings(),
        adminApi.getExperimentalStats()
      ])
      if (settingsRes.status === 'fulfilled') {
        const data = settingsRes.value.data.data.settings
        setSettings(data)
        setTrial(data.trial)
        setFeatures(data.features)
      } else {
        throw settingsRes.reason
      }
      if (statsRes.status === 'fulfilled') {
        setExpStats(statsRes.value.data.data)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveTrial = async () => {
    try {
      setSaving(true)
      setError(null)
      await adminApi.updateTrialSettings(trial)
      setSuccess('Impostazioni trial salvate')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveFeatures = async () => {
    try {
      setSaving(true)
      setError(null)
      await adminApi.updateFeatures(features)
      setSuccess('Feature flags salvati')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const Toggle = ({ checked, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        {description && <p className="text-sm text-gray-500">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-primary-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Impostazioni Sistema</h1>
        <p className="text-gray-500">Configura trial, feature flags e comportamento globale</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Trial Settings */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Impostazioni Trial</h2>
          </div>
          <button
            onClick={handleSaveTrial}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durata Trial (giorni)
            </label>
            <input
              type="number"
              min="7"
              max="365"
              value={trial.durationDays}
              onChange={(e) => setTrial({ ...trial, durationDays: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-sm text-gray-500">Durata default per nuovi centri</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Grace Period (giorni)
            </label>
            <input
              type="number"
              min="7"
              max="90"
              value={trial.gracePeriodDays}
              onChange={(e) => setTrial({ ...trial, gracePeriodDays: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-sm text-gray-500">Giorni prima della cancellazione dopo scadenza</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Minori (Trial)
            </label>
            <input
              type="number"
              min="1"
              value={trial.limits.maxMinori}
              onChange={(e) => setTrial({
                ...trial,
                limits: { ...trial.limits, maxMinori: parseInt(e.target.value) }
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Educatori (Trial)
            </label>
            <input
              type="number"
              min="1"
              value={trial.limits.maxEducatori}
              onChange={(e) => setTrial({
                ...trial,
                limits: { ...trial.limits, maxEducatori: parseInt(e.target.value) }
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Feature Flags */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Settings className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Feature Flags</h2>
          </div>
          <button
            onClick={handleSaveFeatures}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva
          </button>
        </div>

        <div className="divide-y">
          <Toggle
            checked={features.registrationEnabled}
            onChange={(v) => setFeatures({ ...features, registrationEnabled: v })}
            label="Registrazione Abilitata"
            description="Permetti la registrazione di nuovi centri"
          />
          <Toggle
            checked={features.betaMode}
            onChange={(v) => setFeatures({ ...features, betaMode: v })}
            label="Modalità Beta"
            description="Mostra badge beta nell'interfaccia"
          />
          <Toggle
            checked={features.paymentsEnabled}
            onChange={(v) => setFeatures({ ...features, paymentsEnabled: v })}
            label="Pagamenti Abilitati"
            description="Abilita i pagamenti con Stripe"
          />
          <Toggle
            checked={features.emailEnabled}
            onChange={(v) => setFeatures({ ...features, emailEnabled: v })}
            label="Email Abilitate"
            description="Invia email reali (altrimenti dry-run)"
          />
          <Toggle
            checked={features.maintenanceMode}
            onChange={(v) => setFeatures({ ...features, maintenanceMode: v })}
            label="Modalità Manutenzione"
            description="Blocca l'accesso al sistema"
          />
        </div>

        {features.maintenanceMode && (
          <div className="mt-4 pt-4 border-t">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Messaggio Manutenzione
            </label>
            <textarea
              value={features.maintenanceMessage}
              onChange={(e) => setFeatures({ ...features, maintenanceMessage: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Il sistema è in manutenzione..."
            />
          </div>
        )}
      </div>

      {/* Experimental Features */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <FlaskConical className="h-5 w-5 text-purple-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Feature Sperimentali</h2>
          </div>
          <button
            onClick={handleSaveFeatures}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Le feature sperimentali richiedono abilitazione globale (qui) e poi per-centro nella scheda del singolo tenant.
        </p>

        {/* PEP Toggle */}
        <div className="border border-gray-200 rounded-lg p-4">
          <Toggle
            checked={features.pepExperimental || false}
            onChange={(v) => setFeatures({ ...features, pepExperimental: v })}
            label="PEP — Progetto Educativo Personalizzato"
            description="Abilita il modulo PEP sperimentale. Richiede abilitazione per-centro."
          />

          {/* Chips */}
          <div className="flex flex-wrap gap-2 mt-3 ml-1">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
              <Target className="h-3 w-3" /> Progetto Educativo
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full">
              <GitBranch className="h-3 w-3" /> Timeline
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">
              <Lightbulb className="h-3 w-3" /> Insight
            </span>
          </div>

          {/* Stats */}
          {expStats?.pep && (
            <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xl font-bold text-gray-900">{expStats.pep.tenantsEnabled}</p>
                <p className="text-xs text-gray-500">Centri abilitati</p>
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{expStats.pep.tenantsEligible}</p>
                <p className="text-xs text-gray-500">Centri idonei</p>
              </div>
              <div>
                <p className={`text-xl font-bold ${features.pepExperimental ? 'text-green-600' : 'text-gray-400'}`}>
                  {features.pepExperimental ? 'ON' : 'OFF'}
                </p>
                <p className="text-xs text-gray-500">Flag globale</p>
              </div>
            </div>
          )}
        </div>

        {/* UX Innovations Toggles */}
        <h4 className="text-sm font-semibold text-gray-700 mt-6 mb-3">Innovazioni UX</h4>

        <div className="space-y-3">
          <div className="border border-gray-200 rounded-lg p-4">
            <Toggle
              checked={features.quickCheckinEnabled || false}
              onChange={(v) => setFeatures({ ...features, quickCheckinEnabled: v })}
              label="Quick Check-in"
              description="Griglia rapida per segnare le presenze giornaliere"
            />
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <Toggle
              checked={features.attivitaGruppoEnabled || false}
              onChange={(v) => setFeatures({ ...features, attivitaGruppoEnabled: v })}
              label="Attività di Gruppo"
              description="Registra attività per più minori contemporaneamente"
            />
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <Toggle
              checked={features.smartAlertsEnabled || false}
              onChange={(v) => setFeatures({ ...features, smartAlertsEnabled: v })}
              label="Smart Alerts"
              description="Insight automatici su pattern e anomalie per coordinatori"
            />
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <Toggle
              checked={features.prepColloquioEnabled || false}
              onChange={(v) => setFeatures({ ...features, prepColloquioEnabled: v })}
              label="Prep Colloquio"
              description="Dossier auto-generato per colloqui con genitori/servizi"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
